import { DisplayText, displayKey, displayText } from '@kikkz/shared/i18n/util';
import { ILoggingService } from '@kikkz/shared/logging/util';
import {
  AdditionalQuestionType,
  Competition,
  Fixture,
  FixtureEarnedPoints,
  FixtureMatchStats,
  fixtureRoundTypeKey,
  FixtureStatus,
  FirstGoalType,
  firstGoalToDialValue,
  formatFirstGoalDial,
  formatMatchMinute,
  GameScoreValues,
  HallOfFameStats,
  HallOfFameUser,
  HallOfFameUserRecord,
  isAdditionalQuestionType,
  isFirstGoalType,
  isFixtureRoundType,
  isFixtureState,
  isPlayerPosition,
  isRankingCompetition,
  legacyMinuteToFirstGoal,
  MatchdayPoints,
  MatchdayPredictionDetail,
  MatchdayWinner,
  MatchdayWinners,
  Player,
  PlayerPosition,
  PredictionGameTenantConfig,
  RankingCompetition,
  RankingEntry,
  RankingFilterOption,
  RankingPage,
  RankingUserDetail,
  selectablePlayers,
  Team,
  UserGroup,
  UserPredictionStats,
} from '@kikkz/shared/prediction-game/util';
import type { components } from '../api/backend-types';

type PGFixture = components['schemas']['PGFixture'];
type PGFixtureUserPrediction = components['schemas']['PGFixtureUserPrediction'];
type PGFixtureMatchStats = components['schemas']['PGFixtureMatchStats'];
type PGFixtureLineupPlayer = components['schemas']['PGFixtureLineupPlayer'];
type PGFixtureMatchStatsGoalScorer = components['schemas']['PGFixtureMatchStatsGoalScorer'];
type PGUserPredictionStats = components['schemas']['PGUserPredictionStats'];
type PGUserGameScore = components['schemas']['PGUserGameScore'];
type PGTeam = components['schemas']['PGTeam'];
type PGSquadPlayer = components['schemas']['PGSquadPlayer'];
type PGUserRankingList = components['schemas']['PGUserRankingList'];
type PGUserRankingListFilterOption = components['schemas']['PGUserRankingListFilterOption'];
type PGUserRanking = components['schemas']['PGUserRanking'];
type PGUserStats = components['schemas']['PGUserStats'];
type PGGroup = components['schemas']['PGGroup'];
type PGGameScoreValues = components['schemas']['PGGameScoreValues'];
type PGHallOfFameStats = components['schemas']['PGHallOfFameStats'];
type PGFixturePredictionGameResult = components['schemas']['PGFixturePredictionGameResult'];
// The backend's generic account DTO — aliased because `User` alone would read like a
// frontend model here, and this file maps DTOs onto those.
type BackendUser = components['schemas']['User'];

/**
 * Warn channel the mapper uses whenever the backend sends something the frontend does not
 * model: an unknown enum value, a missing id. The mapper still returns a usable object —
 * it never throws on a surprising payload — but the surprise must not vanish silently.
 *
 * It is a plain function parameter rather than an injected service because the mappers are
 * free functions: the caller (an `@Injectable` with `LOGGING_SERVICE`) builds one with
 * {@link createMapperWarn} and hands it down. That keeps the mappers pure and directly
 * unit-testable, and avoids any module-level logger state shared between requests.
 */
export type MapperWarn = (
  message: string,
  context: Record<string, unknown>,
  firstSeen?: Record<string, unknown>,
) => void;

/**
 * Builds a {@link MapperWarn} that warns ONCE per distinct message + `context`.
 *
 * One response maps up to a few hundred fixtures; without the deduplication a single
 * unknown enum value would produce a few hundred identical log lines. That only works if
 * the deduplication key is free of per-entity data, which is why a warning is split in
 * two: `context` carries what makes the problem distinct (the unknown value) and is part
 * of the key, while `firstSeen` carries the identifying example (the fixture id, the
 * group name) and is logged with — but does not multiply — the single line.
 *
 * The `Set` lives in the returned closure, so a mapping pass is deduplicated against
 * itself only — build a fresh one per request (see `HttpPredictionGameService`) and a
 * problem that persists still shows up again on the next request.
 */
export function createMapperWarn(logger: ILoggingService): MapperWarn {
  const seen = new Set<string>();
  return (message, context, firstSeen) => {
    const key = `${message}|${JSON.stringify(context)}`;
    if (seen.has(key)) return;
    seen.add(key);
    logger.warn(`[prediction-game.mapper] ${message}`, firstSeen ? { ...context, firstSeen } : context);
  };
}

// Bundled club logos for teams whose CDN image (static.kikkz.de/.../team/{id}.png) is
// missing in the dev/test data (returns 403). The tenant config lists the backend team
// ids = the URL's filename; when listed, the local asset is used instead. Unknown teams
// keep the backend URL and fall back to the neutral placeholder via `kikkzImgFallback`.
export function localizeTeamLogo(url: string, tenant: PredictionGameTenantConfig): string {
  const id = /\/team\/(\d+)\.png/.exec(url)?.[1];
  return id && tenant.localTeamLogoIds.includes(id) ? `/assets/img/teams/${id}.png` : url;
}

// --- mapping helpers -------------------------------------------------------

// The backend returns fixtures unordered; the model guarantees chronological order
// (kickoff ascending), so consumers never have to sort themselves. Typed on the
// timestamp-carrying shape rather than on one model: every model that sorts by kick-off
// keys it on the same ISO `startingAt`, and ISO-8601 strings compare lexicographically
// exactly as they do chronologically.
export function byKickoff(a: { startingAt: string }, b: { startingAt: string }): number {
  return a.startingAt.localeCompare(b.startingAt);
}

/** The reverse of {@link byKickoff}: newest kick-off first, for lists that look backwards. */
export function byKickoffDesc(a: { startingAt: string }, b: { startingAt: string }): number {
  return byKickoff(b, a);
}

/**
 * Whether a matchday has anything to show. Winners are determined 24h after kick-off, so
 * the backend also returns fixtures it has not evaluated yet — a "Spieltagssieger" row
 * without a single winner is an empty promise. Named like {@link selectRelevantFixtures}:
 * which rows are relevant is a rule of the domain, not of the transport that applies it.
 */
export function hasWinners(matchday: MatchdayWinners): boolean {
  return matchday.winners.length > 0;
}

function toTeam(team: PGTeam | undefined, tenant: PredictionGameTenantConfig): Team {
  return { name: team?.name ?? '', imageUrl: localizeTeamLogo(team?.imageUrl ?? '', tenant) };
}

function toCompetition(dto: PGFixture): Competition {
  return { name: dto.competitionName ?? '', imageUrl: dto.competitionImageUrl ?? '' };
}

/**
 * The position decides the possible points of a first-goal-scorer tip and the picker's
 * grouping, so an unknown one must not be passed through as if the UI understood it.
 * Documented fallback: `midfielder`, the position every formation has the most of — the
 * player stays selectable instead of disappearing from the squad.
 *
 * Widened to `string` for the same reason as the question type: the generated union only
 * lists what the spec knew at codegen time, not what arrives on the wire.
 */
const PLAYER_POSITION_FALLBACK: PlayerPosition = 'midfielder';

/**
 * The first-goal kind drives the dial (and with it the whole "minute of the first goal"
 * question). An unknown kind has no dial ordinal, so it cannot be rendered — reported
 * once and dropped to `undefined`, which the dial reads as "not answered".
 */
function toFirstGoalType(
  value: string | null | undefined,
  firstSeen: Record<string, unknown>,
  warn: MapperWarn,
): FirstGoalType | null {
  if (value == null) return null;
  if (isFirstGoalType(value)) return value;
  warn('Unknown firstGoalType — the first-goal answer is dropped', { value }, firstSeen);
  return null;
}

function toPlayerPosition(
  value: string | null | undefined,
  firstSeen: Record<string, unknown>,
  warn: MapperWarn,
): PlayerPosition {
  // Absent field: the documented default, not a surprise — no warning.
  if (value == null) return PLAYER_POSITION_FALLBACK;
  if (isPlayerPosition(value)) return value;
  warn(`Unknown player position — falling back to ${PLAYER_POSITION_FALLBACK}`, { value }, firstSeen);
  return PLAYER_POSITION_FALLBACK;
}

export function toPlayer(dto: PGSquadPlayer, warn: MapperWarn): Player {
  const raw = dto as { isActive?: boolean; active?: boolean };
  return {
    id: dto.id ?? '',
    firstName: dto.firstName ?? '',
    lastName: dto.lastName ?? '',
    name: dto.displayName ?? `${dto.firstName ?? ''} ${dto.lastName ?? ''}`.trim(),
    imageUrl: dto.imageUrlLargeRegular ?? dto.imageUrlSmallRegular ?? '',
    imageUrlCelebrating: dto.imageUrlLargeCelebrating ?? '',
    position: toPlayerPosition(dto.position, { playerId: dto.id ?? '' }, warn),
    isActive: raw.isActive ?? raw.active ?? true,
    // Per-player scoring is not provided by the backend (see gap analysis).
    scoreLineup: 0,
    scoreFirstGoalScorer: 0,
  };
}

export function toGameScoreValues(dto: PGGameScoreValues, tenant: PredictionGameTenantConfig): GameScoreValues {
  const byPosition = (dto.maxScoreFirstGoalScorerByPosition ?? {}) as Partial<Record<PlayerPosition, number>>;
  return {
    maxScoreMatchResult: dto.maxScoreMatchResult ?? 0,
    maxScoreFirstGoalScorerByPosition: byPosition,
    // Fallback for backends that don't send maxScoreFirstGoalScorerNoGoal yet.
    maxScoreFirstGoalScorerNoGoal: dto.maxScoreFirstGoalScorerNoGoal ?? tenant.noGoalPointsFallback,
    maxScoreLineupByNumberOfChosenPlayers: dto.maxScoreLineupByNumberOfChosenPlayers ?? {},
    maxScoreShotsOnGoal: dto.maxScoreShotsOnGoal ?? 0,
    maxScoreCorners: dto.maxScoreCorners ?? 0,
    maxScoreMatchMinuteOfFirstGoal: dto.maxScoreMatchMinuteOfFirstGoal ?? 0,
    maxScoreBallPossession: dto.maxScoreBallPossession ?? 0,
    statisticMaxValue: dto.statisticMaxValue ?? null,
  };
}

/**
 * The live API prefixes fixture booleans with `is` (isHomeTeam, isFinished) while the
 * OpenAPI spec/type drops the prefix (homeTeam, finished). This type covers both shapes.
 * TODO-koeln: Spec/Live-Drift im Backend beheben, dann Shim löschen.
 */
export type PGFixtureLive = PGFixture & { isHomeTeam?: boolean; isFinished?: boolean; finished?: boolean };

/** A fixture DTO whose drifting boolean flags have been resolved to the spec names. */
export type NormalizedPGFixture = PGFixture & { homeTeam: boolean; finished: boolean };

/**
 * Resolves the spec/live flag drift exactly once per DTO, right at the response boundary,
 * so every mapper downstream can read the flags type-safely.
 */
export function normalizeFixtureDto(dto: PGFixtureLive): NormalizedPGFixture {
  return {
    ...dto,
    homeTeam: dto.isHomeTeam ?? dto.homeTeam ?? true,
    finished: dto.isFinished ?? dto.finished ?? false,
  };
}

function deriveStatus(dto: NormalizedPGFixture, fixtureId: string, warn: MapperWarn): FixtureStatus {
  // The backend now exposes a richer lifecycle via `state`; map it onto the three
  // UI states. ABANDONED is treated as over (FINISHED); PLANNED/POSTPONED/CANCELLED
  // are "not played yet" (SCHEDULED).
  //
  // A state the frontend does not model would otherwise slip into the legacy branch
  // below and silently be presented as SCHEDULED/LIVE — report it, then let the legacy
  // flags decide, which is still the best guess available.
  if (dto.state != null && !isFixtureState(dto.state)) {
    warn(
      'Unknown fixture state — falling back to the legacy finished/editable flags',
      { value: dto.state },
      {
        fixtureId,
      },
    );
  }
  switch (dto.state) {
    case 'ONGOING':
      return 'LIVE';
    case 'FINISHED':
    case 'ABANDONED':
      return 'FINISHED';
    case 'PLANNED':
    case 'POSTPONED':
    case 'CANCELLED':
      return 'SCHEDULED';
  }
  // Fallback for the older API shape without `state`: legacy finished/editable flags.
  const editable = dto.userPredictionEditable ?? true;
  if (dto.finished) return 'FINISHED';
  return editable ? 'SCHEDULED' : 'LIVE';
}

function toMatchdayLabel(dto: PGFixture): DisplayText {
  const day = dto.matchDay;
  // REGULAR league fixtures use the `pg.fixture.matchday` key ("<n>. Spieltag"). Cup / leg
  // fixtures (matchType/roundType) use the roundType: numbered rounds ("ROUND") use
  // `pg.fixture.round` ("<n>. Runde"), knockout rounds their own `pg.fixture.roundType.*` key
  // without a number. Missing/unknown roundType falls back to the bare number, which is data,
  // not copy — hence a literal `displayText`.
  if ((dto.matchType ?? 'REGULAR') === 'REGULAR') {
    return day != null ? displayKey('pg.fixture.matchday', { day }) : displayText('');
  }
  const roundType = dto.roundType;
  if (roundType === 'ROUND') {
    return day != null ? displayKey('pg.fixture.round', { day }) : displayText('');
  }
  if (isFixtureRoundType(roundType)) {
    return displayKey(fixtureRoundTypeKey(roundType));
  }
  return day != null ? displayText(String(day)) : displayText('');
}

/**
 * Documented fallback for a missing or unknown `additionalQuestionType`: the shots-on-goal
 * question is the one every tenant/backend supports, so an unknown type degrades to a
 * question the UI can render rather than to an empty card.
 */
const ADDITIONAL_QUESTION_FALLBACK: AdditionalQuestionType = 'SHOTS_ON_GOAL';

/**
 * The generated backend type lists only the enum values the spec knew at codegen time, so
 * the raw value is deliberately widened to `string` before the guard: what actually arrives
 * on the wire is not constrained by the spec.
 */
function toAdditionalQuestionType(
  value: string | null | undefined,
  fixtureId: string,
  warn: MapperWarn,
): AdditionalQuestionType {
  // Absent field: the documented default, not a surprise — no warning.
  if (value == null) return ADDITIONAL_QUESTION_FALLBACK;
  if (isAdditionalQuestionType(value)) return value;
  warn(`Unknown additionalQuestionType — falling back to ${ADDITIONAL_QUESTION_FALLBACK}`, { value }, { fixtureId });
  return ADDITIONAL_QUESTION_FALLBACK;
}

function toEarnedPoints(
  score: PGUserGameScore | null | undefined,
  userRank: number | null | undefined,
): FixtureEarnedPoints | null {
  if (!score) return null;
  return {
    total: score.score ?? 0,
    result: score.scoreMatchResult ?? 0,
    goalScorer: score.scoreFirstGoalScorer ?? 0,
    lineup: score.scoreLineup ?? 0,
    bonusQuestion: score.scoreAdditionalQuestion ?? 0,
    // "Platz X an diesem Spieltag" — provided per-fixture by the backend.
    matchdayRank: userRank ?? null,
  };
}

export function toFixture(
  rawDto: PGFixtureLive,
  squad: Player[],
  tenant: PredictionGameTenantConfig,
  warn: MapperWarn,
): Fixture {
  const dto = normalizeFixtureDto(rawDto);
  const byId = new Map(squad.map((p) => [p.id, p]));
  const fixtureId = dto.id ?? '';
  // An id-less fixture still renders, but every id-keyed action on it (saving a
  // prediction: PUT .../fixture/{id}) is impossible — see the guard in the HTTP service.
  if (!fixtureId) {
    warn('Fixture without id — predictions for it cannot be saved', {}, { startingAt: dto.startingAt ?? null });
  }
  const status = deriveStatus(dto, fixtureId, warn);
  // Fail closed: a missing editability flag must lock the question, never unlock it —
  // the backend rejects late saves anyway, so defaulting to true only fakes editability.
  const userPredictionEditable = dto.userPredictionEditable ?? false;
  const userPredictionLineupEditable = dto.userPredictionLineupEditable ?? false;
  return {
    id: fixtureId,
    providerId: dto.providerId ?? undefined,
    startingAt: dto.startingAt ?? '',
    teamIsHome: dto.homeTeam,
    isFinished: status === 'FINISHED',
    userPredictionEditable,
    userPredictionLineupEditable,
    team: tenant.team,
    opponent: toTeam(dto.opponent, tenant),
    matchday: toMatchdayLabel(dto),
    competition: toCompetition(dto),
    // Pickers must not offer players no longer in the squad (isActive: false) — but a
    // FINISHED fixture may be months old, so its (read-only) views keep the full squad
    // to still resolve/show players who have since left.
    teamToChooseFrom: status === 'FINISHED' ? squad : selectablePlayers(squad),
    additionalQuestionType: toAdditionalQuestionType(dto.additionalQuestionType, fixtureId, warn),
    userPrediction: toUserPrediction(dto.userPrediction, fixtureId, byId, warn),
    // Fail closed: während ONGOING liefert das Backend teils falsche/verfrühte
    // Interimsdaten (matchStats, userGameScore). Im LIVE-Zustand zeigt das Frontend
    // laut Figma-Ongoing-Design grundsätzlich keine Zwischenstände/Punkte — daher
    // werden sie hier zentral genullt, unabhängig davon, was das Backend schickt.
    matchStats: status === 'LIVE' ? null : toMatchStats(dto.matchStats, fixtureId, byId, warn),
    userPredictionStats: toUserPredictionStats(dto.userPredictionStats),
    status,
    // Per-fixture earned points + matchday rank now come from the fixture itself.
    earnedPoints: status === 'LIVE' ? null : toEarnedPoints(dto.userGameScore, dto.userRank),
  };
}

/**
 * Prefer the new `firstGoalType` contract; fall back to decoding the legacy
 * `matchMinuteOfFirstGoal` encoding (0 = no goal, 91 = 2nd-half stoppage) for old data.
 * An unknown `firstGoalType` is reported and treated as "not answered" rather than
 * silently decoded from the legacy minute, which would invent an answer.
 */
function toFirstGoal(
  dto: PGFixtureUserPrediction,
  fixtureId: string,
  warn: MapperWarn,
): { type: FirstGoalType; minute?: number } | null {
  if (dto.firstGoalType == null) return legacyMinuteToFirstGoal(dto.matchMinuteOfFirstGoal);
  const type = toFirstGoalType(dto.firstGoalType, { fixtureId }, warn);
  return type ? { type, minute: dto.matchMinuteOfFirstGoal ?? undefined } : null;
}

function resolveLineup(
  ids: (string | undefined)[] | null | undefined,
  byId: Map<string, Player>,
): Player[] | undefined {
  if (!ids) return undefined;
  return ids.map((id) => (id ? byId.get(id) : undefined)).filter((p): p is Player => !!p);
}

// Match-stats players may not be linked to a squad entry (`squadPlayerId: null`) — a valid case
// (e.g. loaned/short-notice players). Prefer the full squad player when linked; otherwise build a
// display-only Player from the backend-provided name/position so the actual lineup / scorer can
// still be shown in full.
function toLineupPlayer(
  p: PGFixtureLineupPlayer | PGFixtureMatchStatsGoalScorer,
  byId: Map<string, Player>,
  fixtureId: string,
  warn: MapperWarn,
): Player {
  const squadPlayer = p.squadPlayerId ? byId.get(p.squadPlayerId) : undefined;
  if (squadPlayer) return squadPlayer;
  return {
    id: p.squadPlayerId ?? p.providerId ?? '',
    firstName: '',
    lastName: '',
    name: p.displayName ?? '',
    imageUrl: '',
    imageUrlCelebrating: '',
    position: toPlayerPosition(p.position, { fixtureId, displayName: p.displayName ?? '' }, warn),
    isActive: true,
    scoreLineup: 0,
    scoreFirstGoalScorer: 0,
  };
}

function toUserPrediction(
  dto: PGFixtureUserPrediction | null | undefined,
  fixtureId: string,
  byId: Map<string, Player>,
  warn: MapperWarn,
): FixtureMatchStats | null {
  if (!dto) return null;
  const scorerId = dto.firstGoalScorerSquadPlayerId ?? undefined;
  const firstGoal = toFirstGoal(dto, fixtureId, warn);
  return {
    fixtureId,
    resultTeam: dto.resultTeam ?? undefined,
    resultOpponent: dto.resultOpponent ?? undefined,
    firstGoalScorer: scorerId ? byId.get(scorerId) : undefined,
    firstGoalScorerNoGoal: dto.firstGoalScorerNoGoal ?? undefined,
    lineup: resolveLineup(dto.lineupSquadPlayerIds, byId),
    shotsOnGoal: dto.shotsOnGoal ?? undefined,
    corners: dto.corners ?? undefined,
    firstGoalType: firstGoal?.type,
    // The minute only carries meaning for IN_REGULAR_TIME; otherwise it must stay unset.
    matchMinuteOfFirstGoal: firstGoal?.type === 'IN_REGULAR_TIME' ? (firstGoal.minute ?? undefined) : undefined,
    ballPossessionPercentage: dto.ballPossessionPercentage ?? undefined,
  };
}

/** First own-team goal scorer of the match — ordered by `orderNumber` (fallback: minute). */
function firstGoalScorerOf(dto: PGFixtureMatchStats): PGFixtureMatchStatsGoalScorer | undefined {
  return [...(dto.goalScorerTeam ?? [])].sort(
    (a, b) => (a.orderNumber ?? a.matchMinute ?? 0) - (b.orderNumber ?? b.matchMinute ?? 0),
  )[0];
}

function toMatchStats(
  dto: PGFixtureMatchStats | null | undefined,
  fixtureId: string,
  byId: Map<string, Player>,
  warn: MapperWarn,
): FixtureMatchStats | null {
  if (!dto) return null;
  const first = firstGoalScorerOf(dto);
  return {
    fixtureId,
    resultTeam: dto.resultTeam ?? undefined,
    resultOpponent: dto.resultOpponent ?? undefined,
    firstGoalScorer: first ? toLineupPlayer(first, byId, fixtureId, warn) : undefined,
    lineup: dto.lineupPlayers?.map((p) => toLineupPlayer(p, byId, fixtureId, warn)),
    shotsOnGoal: dto.shotsOnGoal ?? undefined,
    corners: dto.corners ?? undefined,
    matchMinuteOfFirstGoal: first?.matchMinute ?? undefined,
    ballPossessionPercentage: dto.ballPossessionPercentage ?? undefined,
  };
}

function toUserPredictionStats(dto: PGUserPredictionStats | null | undefined): UserPredictionStats | null {
  if (!dto) return null;
  return {
    totalPredictions: dto.totalPredictions ?? 0,
    otherPredictionsWithSameResult: dto.otherPredictionsWithSameResult ?? null,
    otherPredictionsWithSameFirstGoalScorer: dto.otherPredictionsWithSameFirstGoalScorer ?? null,
    otherPredictionsWithSameLineup: dto.otherPredictionsWithSameLineup ?? null,
    otherPredictionsWithSameAnswerForAdditionalQuestion:
      dto.otherPredictionsWithSameAnswerForAdditionalQuestion ?? null,
  };
}

export function toUserPredictionDto(prediction: FixtureMatchStats): PGFixtureUserPrediction {
  return {
    fixtureId: prediction.fixtureId,
    resultTeam: prediction.resultTeam ?? null,
    resultOpponent: prediction.resultOpponent ?? null,
    firstGoalScorerSquadPlayerId: prediction.firstGoalScorer?.id ?? null,
    firstGoalScorerNoGoal: prediction.firstGoalScorerNoGoal ?? false,
    lineupSquadPlayerIds: prediction.lineup?.map((p) => p.id) ?? null,
    shotsOnGoal: prediction.shotsOnGoal ?? null,
    corners: prediction.corners ?? null,
    firstGoalType: prediction.firstGoalType ?? null,
    // The backend only accepts a minute (1–90) for IN_REGULAR_TIME; null otherwise.
    matchMinuteOfFirstGoal:
      prediction.firstGoalType === 'IN_REGULAR_TIME' ? (prediction.matchMinuteOfFirstGoal ?? null) : null,
    ballPossessionPercentage: prediction.ballPossessionPercentage ?? null,
  };
}

/**
 * `null` competition means "no filter" — the overall season ranking. An unknown value
 * degrades to exactly that: the option stays selectable and the backend still resolves it
 * (it is echoed back verbatim by `toRankingFilterOptionDto`), only the competition-specific
 * UI treatment is skipped. Widened to `string` for the same reason as the question type:
 * the wire is not bound by the spec's enum.
 */
function toRankingFilterCompetition(
  value: string | null | undefined,
  title: string,
  warn: MapperWarn,
): RankingCompetition | null {
  if (value == null) return null;
  if (isRankingCompetition(value)) return value;
  warn('Unknown ranking competition — treating the option as the overall ranking', { value }, { title });
  return null;
}

export function toRankingFilterOption(dto: PGUserRankingListFilterOption, warn: MapperWarn): RankingFilterOption {
  return {
    competition: toRankingFilterCompetition(dto.competition, dto.title ?? '', warn),
    title: dto.title ?? '',
    subtitle: dto.subtitle ?? null,
    orderIndex: dto.orderIndex ?? 0,
  };
}

/**
 * The backend expects the previously served option back verbatim as the request body —
 * ALL fields, `subtitle` included. It matches on the whole object, so dropping a field
 * it handed out answers 404 (`errorCode: 20002`).
 */
export function toRankingFilterOptionDto(option: RankingFilterOption): PGUserRankingListFilterOption {
  return {
    competition: option.competition,
    title: option.title,
    subtitle: option.subtitle,
    orderIndex: option.orderIndex,
  };
}

function toRankingEntry(dto: PGUserRanking, currentUserId: string | undefined, warn: MapperWarn): RankingEntry {
  // The user id keys the user-stats lookup behind the profile modal. Without it the row
  // still renders, but tapping it cannot open a profile.
  if (!dto.user?.id) {
    warn('Ranking entry without user id — its profile cannot be opened', {}, { name: dto.user?.username ?? '' });
  }
  return {
    id: dto.user?.id,
    rank: dto.rank ?? null,
    previousRank: dto.previousRank ?? null,
    name: dto.user?.username ?? '',
    isMember: !!dto.user?.member,
    points: dto.score ?? 0,
    results: dto.scoreMatchResult ?? 0,
    goalScorers: dto.scoreFirstGoalScorer ?? 0,
    lineups: dto.scoreLineup ?? 0,
    bonusQuestions: dto.scoreAdditionalQuestion ?? 0,
    isCurrentUser: !!currentUserId && dto.user?.id === currentUserId,
  };
}

export function toRankingPage(list: PGUserRankingList, warn: MapperWarn): RankingPage {
  const currentUserId = list.userRanking?.user?.id ?? undefined;
  return {
    entries: (list.topRankings ?? []).map((r) => toRankingEntry(r, currentUserId, warn)),
    currentUserEntry: list.userRanking ? toRankingEntry(list.userRanking, currentUserId, warn) : null,
  };
}

/**
 * Group ranking with provisional ranks for members the backend has not ranked yet
 * (rankings are recalculated every ~10 min, so a freshly created/joined group shows
 * `rank: null`). A group ranking lists all members, so the provisional rank is simply
 * 1 + the number of members with more points — "1." for the founder of a new group,
 * last place for someone who just joined. Ranks delivered by the backend are kept as-is.
 */
export function toGroupRankingPage(list: PGUserRankingList, warn: MapperWarn): RankingPage {
  const page = toRankingPage(list, warn);
  const provisionalRank = (entry: RankingEntry): number =>
    entry.rank ?? 1 + page.entries.filter((other) => other.points > entry.points).length;
  const entries = page.entries.map((entry) => ({ ...entry, rank: provisionalRank(entry) }));
  const currentUserEntry = page.currentUserEntry
    ? { ...page.currentUserEntry, rank: provisionalRank(page.currentUserEntry) }
    : null;
  return { entries, currentUserEntry };
}

export function toUserGroup(group: PGGroup, warn: MapperWarn, ranking?: RankingPage): UserGroup {
  const entries = ranking?.entries ?? [];
  const memberCount = entries.length;
  const averagePoints = memberCount ? entries.reduce((sum, e) => sum + e.points, 0) / memberCount : 0;
  // Every group action (detail, ranking, leave) is keyed by the id — the row renders, but
  // an id-less group is a dead end; the HTTP service rejects the request rather than
  // sending `/group//ranking`.
  if (!group.id) {
    warn('Group without id — it cannot be opened or left', {}, { name: group.name ?? '' });
  }
  return {
    id: group.id ?? '',
    name: group.name ?? '',
    averagePoints,
    memberCount,
    myRank: ranking?.currentUserEntry?.rank ?? null,
  };
}

/**
 * Holders of a record. A holder without an id is dropped: the id keys the profile modal,
 * and a name alone would render a row that cannot be opened (the warning keeps the loss
 * visible).
 */
function toHallOfFameHolders(users: BackendUser[] | undefined, warn: MapperWarn): HallOfFameUser[] {
  const holders: HallOfFameUser[] = [];
  for (const user of users ?? []) {
    if (!user.id) {
      warn('Hall of Fame holder without user id — dropped', {}, { name: user.username ?? '' });
      continue;
    }
    holders.push({ id: user.id, name: user.username ?? '' });
  }
  return holders;
}

/** A record only exists with a value AND at least one holder — otherwise nobody holds it yet. */
function toHallOfFameRecord(
  value: number | undefined,
  users: BackendUser[] | undefined,
  warn: MapperWarn,
): HallOfFameUserRecord {
  const holders = toHallOfFameHolders(users, warn);
  return { value: holders.length ? (value ?? null) : null, holders };
}

export function toHallOfFameStats(dto: PGHallOfFameStats, warn: MapperWarn): HallOfFameStats {
  const groupNames = (dto.maxGroupMembersBy ?? []).filter((name) => !!name);
  const bestMonths = (dto.monthMaxScores ?? []).flatMap((entry) => {
    if (!entry.year || !entry.month) return [];
    return [{ year: entry.year, month: entry.month, ...toHallOfFameRecord(entry.maxScore, entry.maxScoreBy, warn) }];
  });
  bestMonths.sort((a, b) => b.year - a.year || b.month - a.month);
  return {
    bestFixture: toHallOfFameRecord(dto.maxScoreInSingleFixture, dto.maxScoreInSingleFixtureBy, warn),
    bestMonths,
    longestCorrectResultStreak: toHallOfFameRecord(
      dto.maxCorrectResultStreakLength,
      dto.maxCorrectResultStreakLengthBy,
      warn,
    ),
    largestGroup: { value: groupNames.length ? (dto.maxGroupMembers ?? null) : null, groupNames },
  };
}

// --- matchday winners ------------------------------------------------------

/**
 * One winner row. The backend sends its generic account DTO, of which the list needs
 * exactly three things — id, display name and the member flag behind the "+" icon.
 */
function toMatchdayWinner(user: BackendUser, fixtureId: string, warn: MapperWarn): MatchdayWinner {
  // The name IS the row; without it the fixture would list a nameless winner, which
  // looks like a rendering bug rather than like missing data.
  if (!user.username) {
    warn('Matchday winner without username — the row would stay nameless', {}, { fixtureId, userId: user.id ?? '' });
  }
  return {
    id: user.id,
    name: user.username ?? '',
    isMember: user.member ?? false,
  };
}

/**
 * The winners of one evaluated fixture ("Spieltagssieger").
 *
 * Same split as {@link toMatchdayPoints}: the own club comes from the tenant config, the
 * backend only ever sends the opponent. Fixtures the backend has not evaluated yet carry
 * an empty winner list — filtering those out is the transport's job, not the mapper's, so
 * this function maps whatever it is given.
 */
export function toMatchdayWinners(
  dto: PGFixturePredictionGameResult,
  tenant: PredictionGameTenantConfig,
  warn: MapperWarn,
): MatchdayWinners {
  // The fixture id is the list's track key — two id-less rows would share one identity
  // and Angular would recycle the wrong DOM between them.
  if (!dto.fixtureId) {
    warn(
      'Matchday result without fixture id — its row has no stable track key',
      {},
      { startingAt: dto.startingAt ?? null },
    );
  }
  // Home/away decides the left-right order of the match label. Nothing else in the
  // payload implies it, so a missing flag cannot be derived — the row falls back to an
  // away match rather than claiming a home game the club may never have played.
  if (dto.homeTeam === undefined) {
    warn(
      'Matchday result without homeTeam flag — rendering it as an away match',
      {},
      { fixtureId: dto.fixtureId ?? null },
    );
  }
  const fixtureId = dto.fixtureId ?? '';
  return {
    fixtureId,
    // Keep the ISO timestamp: the list formats it with Angular's DatePipe, which is
    // locale-aware and cannot parse a pre-formatted string.
    startingAt: dto.startingAt ?? '',
    team: tenant.team,
    opponent: toTeam(dto.opponent, tenant),
    teamIsHome: dto.homeTeam ?? false,
    winners: (dto.predictionGameWinners ?? []).map((user) => toMatchdayWinner(user, fixtureId, warn)),
  };
}

// The user-stats endpoint returns the whole season (~200 fixtures, unordered). The modal
// shows everything already played (or live), plus upcoming (PLANNED) fixtures — capped at
// `upcomingFixturesShown` (tenant config), or every one of them when that is `null`. The
// backend delivers a placeholder kick-off date for fixtures that are not officially
// scheduled yet. POSTPONED and CANCELLED fixtures are always dropped.
export function selectRelevantFixtures(
  fixtures: PGFixtureLive[],
  upcomingFixturesShown: number | null,
): PGFixtureLive[] {
  const sorted = [...fixtures].sort((a, b) => (a.startingAt ?? '').localeCompare(b.startingAt ?? ''));
  const relevant: PGFixtureLive[] = [];
  let upcoming = 0;
  for (const fixture of sorted) {
    if (fixture.state === 'FINISHED' || fixture.state === 'ONGOING' || fixture.state === 'ABANDONED') {
      relevant.push(fixture);
    } else if (fixture.state === 'PLANNED' && (upcomingFixturesShown === null || upcoming < upcomingFixturesShown)) {
      relevant.push(fixture);
      upcoming++;
    }
  }
  return relevant;
}

export function toMatchdayPoints(
  rawFixture: PGFixtureLive,
  byId: Map<string, Player>,
  tenant: PredictionGameTenantConfig,
  warn: MapperWarn,
): MatchdayPoints {
  const fixture = normalizeFixtureDto(rawFixture);
  if (!fixture.id) {
    warn(
      'Stats fixture without id — its detail card has no stable track key',
      {},
      { startingAt: fixture.startingAt ?? null },
    );
  }
  // The per-fixture score now lives on the fixture itself (PGUserStats.fixtures[].userGameScore).
  const score = fixture.userGameScore;
  return {
    fixtureId: fixture.id ?? '',
    // Keep the ISO timestamp: the modal formats date, weekday and time via Angular's
    // DatePipe, which is locale-aware and cannot parse a pre-formatted string.
    date: fixture.startingAt ?? '',
    team: tenant.team,
    opponent: toTeam(fixture.opponent, tenant),
    // Same source as `toFixture` / the score orientation in `toPredictionDetails`.
    teamIsHome: fixture.homeTeam,
    competition: toCompetition(fixture),
    points: score?.score ?? null,
    // The per-question max is not provided by the backend; the modal template does
    // not render `maxPoints`, so it stays 0.
    maxPoints: 0,
    // `matchStats` is unreliable here (the live API returns null even for FINISHED
    // fixtures) — the lifecycle `state` is the source of truth.
    played: fixture.state === 'FINISHED',
    results: score?.scoreMatchResult ?? null,
    goalScorers: score?.scoreFirstGoalScorer ?? null,
    lineups: score?.scoreLineup ?? null,
    bonusQuestions: score?.scoreAdditionalQuestion ?? null,
    predictionDetails: toPredictionDetails(fixture, byId, warn),
  };
}

/**
 * Builds the expandable per-question detail cards of the user-stats modal:
 * one card per ANSWERED sub-question (unanswered ones are omitted). When
 * `matchStats` is missing, the "actual" row is hidden via `actualValue: null`.
 *
 * Emits the question kind, not a translation key: the rendering component owns
 * the kind → label mapping. Each `kind` is pushed at most once per fixture, so
 * it is a stable `track` key for the detail cards.
 */
function toPredictionDetails(
  fixture: NormalizedPGFixture,
  byId: Map<string, Player>,
  warn: MapperWarn,
): MatchdayPredictionDetail[] {
  const prediction = fixture.userPrediction;
  if (!prediction) return [];
  const score = fixture.userGameScore;
  const stats = fixture.matchStats ?? null;
  const firstScorer = stats ? firstGoalScorerOf(stats) : undefined;
  // Scores are displayed in home:away orientation — same source as `toFixture`.
  const isHome = fixture.homeTeam;
  const inHomeAwayOrder = (team: number, opponent: number): string =>
    isHome ? `${team}:${opponent}` : `${opponent}:${team}`;
  const details: MatchdayPredictionDetail[] = [];

  if (prediction.resultTeam != null && prediction.resultOpponent != null) {
    const actualValue =
      stats && stats.resultTeam != null && stats.resultOpponent != null
        ? inHomeAwayOrder(stats.resultTeam, stats.resultOpponent)
        : null;
    details.push({
      kind: 'RESULT',
      points: score?.scoreMatchResult ?? 0,
      tippMeaning: 'PREDICTION',
      tippValue: displayText(inHomeAwayOrder(prediction.resultTeam, prediction.resultOpponent)),
      actualValue: actualValue != null ? displayText(actualValue) : null,
    });
  }

  if (prediction.firstGoalScorerSquadPlayerId || prediction.firstGoalScorerNoGoal === true) {
    const tippValue = prediction.firstGoalScorerSquadPlayerId
      ? displayText(byId.get(prediction.firstGoalScorerSquadPlayerId)?.name ?? '–')
      : displayKey('pg.common.noGoal');
    // An empty scorer list on existing matchStats means the team did not score.
    const actualValue = stats
      ? firstScorer
        ? displayText(firstScorer.displayName ?? '–')
        : displayKey('pg.common.noGoal')
      : null;
    details.push({
      kind: 'GOAL_SCORER',
      points: score?.scoreFirstGoalScorer ?? 0,
      tippMeaning: 'PREDICTION',
      tippValue,
      actualValue,
    });
  }

  // The lineup card is always shown (Figma): no lineup tipped -> "-/11".
  // With the actual lineup available, show the hit count ("Richtig getippt: n/11");
  // tipped but no actual lineup yet, only state how many players were picked
  // ("Getippt: n Spieler") - a bare "n/11" would falsely read as a hit count.
  const lineupIds = (prediction.lineupSquadPlayerIds ?? []).filter((id): id is string => !!id);
  const actualIds = stats?.lineupPlayers?.length
    ? new Set(stats.lineupPlayers.map((p) => p.squadPlayerId).filter((id): id is string => !!id))
    : null;
  details.push({
    kind: 'LINEUP',
    points: score?.scoreLineup ?? 0,
    tippMeaning: !lineupIds.length || actualIds ? 'HIT_COUNT' : 'PICK_COUNT',
    tippValue: !lineupIds.length
      ? displayText('-/11')
      : actualIds
        ? displayText(`${lineupIds.filter((id) => actualIds.has(id)).length}/11`)
        : displayKey('pg.stats.detail.lineupPicked', { count: lineupIds.length }),
    actualValue: null,
  });

  const additional = toAdditionalQuestionValues(fixture, prediction, stats, firstScorer, warn);
  if (additional) {
    details.push({
      kind: 'ADDITIONAL_QUESTION',
      points: score?.scoreAdditionalQuestion ?? 0,
      tippMeaning: 'PREDICTION',
      tippValue: additional.tippValue,
      actualValue: additional.actualValue,
    });
  }

  return details;
}

/** Tipp/actual display values for the fixture's additional question; `null` when unanswered. */
function toAdditionalQuestionValues(
  fixture: PGFixture,
  prediction: PGFixtureUserPrediction,
  stats: PGFixtureMatchStats | null,
  firstScorer: PGFixtureMatchStatsGoalScorer | undefined,
  warn: MapperWarn,
): { tippValue: DisplayText; actualValue: DisplayText | null } | null {
  // Same guard as `toFixture`, so an unknown type is reported once here too instead of
  // silently falling through to `default` and hiding the card.
  switch (toAdditionalQuestionType(fixture.additionalQuestionType, fixture.id ?? '', warn)) {
    case 'SHOTS_ON_GOAL':
      if (prediction.shotsOnGoal == null) return null;
      return {
        tippValue: displayText(`${prediction.shotsOnGoal}`),
        actualValue: stats?.shotsOnGoal != null ? displayText(`${stats.shotsOnGoal}`) : null,
      };
    case 'CORNERS':
      if (prediction.corners == null) return null;
      return {
        tippValue: displayText(`${prediction.corners}`),
        actualValue: stats?.corners != null ? displayText(`${stats.corners}`) : null,
      };
    case 'BALL_POSSESSION':
      if (prediction.ballPossessionPercentage == null) return null;
      return {
        tippValue: displayText(`${prediction.ballPossessionPercentage}%`),
        // The backend reports the actual possession as a double — round for display.
        actualValue:
          stats?.ballPossessionPercentage != null
            ? displayText(`${Math.round(stats.ballPossessionPercentage)}%`)
            : null,
      };
    case 'MATCH_MINUTE_OF_FIRST_GOAL': {
      // Same firstGoalType-vs-legacy derivation (and the same guard) as `toUserPrediction`.
      const firstGoal = toFirstGoal(prediction, fixture.id ?? '', warn);
      const dial = firstGoal ? firstGoalToDialValue(firstGoal.type, firstGoal.minute ?? null) : null;
      if (dial == null) return null;
      return {
        tippValue: formatFirstGoalDial(dial),
        // Actual = minute of the first own-team goal; no scorer on existing stats → "Kein Tor".
        actualValue: stats ? formatMatchMinute(firstScorer ? (firstScorer.matchMinute ?? null) : 0) : null,
      };
    }
    // MATCH_RESULT / FIRST_GOAL_SCORER / LINEUP have their own detail cards above — as the
    // "additional" question they carry no extra row. Not a surprise, so no warning.
    default:
      return null;
  }
}

/** Assembles the ranking-user-detail modal payload from the raw userStats DTO + resolved squad. */
export function toRankingUserDetail(
  stats: PGUserStats,
  squad: Player[],
  tenant: PredictionGameTenantConfig,
  warn: MapperWarn,
): RankingUserDetail {
  const byId = new Map(squad.map((p) => [p.id, p]));
  return {
    name: stats.currentUserRanking?.user?.username ?? '',
    totalPoints: stats.currentUserRanking?.score ?? null,
    totalRank: stats.currentUserRanking?.rank ?? null,
    matchdays: selectRelevantFixtures(stats.fixtures ?? [], tenant.upcomingFixturesShown).map((fixture) =>
      toMatchdayPoints(fixture, byId, tenant, warn),
    ),
  };
}
